import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import * as api from '../services/api';
import { supabase } from '../services/supabaseClient';

const AppContext = createContext(null);

const initialState = {
  // Authentication State
  auth: {
    token: localStorage.getItem('token') || null,
    isAuthenticated: !!localStorage.getItem('token'),
    email: localStorage.getItem('auth_email') || '',
    backendUrl: localStorage.getItem('backend_url') || 'https://aishwarya-backend-55345914059.asia-northeast1.run.app',
  },

  clients:      [],
  inward:       [],
  ledger:       [],       // raw movements from /api/stock/ledger
  stockBalance: [],       // aggregated from /api/stock/balance
  workcards:    [],
  invoices:     [],

  // Billing UI state
  bilSel:   [],           // selected workcard IDs
  bilRates: {},           // { [wcId]: rateString }

  // Loading & Error flags
  loading: { clients: false, inward: false, ledger: false, workcards: false, invoices: false },
  errors:  {},

  // Toast queue
  toasts: [],
};

// ─────────────────────────────────────────
// Reducer
// ─────────────────────────────────────────
function reducer(state, action) {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('auth_email', action.payload.email);
      localStorage.setItem('backend_url', action.payload.backendUrl);
      return {
        ...state,
        auth: {
          token: action.payload.token,
          isAuthenticated: true,
          email: action.payload.email,
          backendUrl: action.payload.backendUrl,
        }
      };

    case 'TOKEN_REFRESHED':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        auth: { ...state.auth, token: action.payload.token }
      };

    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('auth_email');
      return {
        ...state,
        clients:      [],
        inward:       [],
        ledger:       [],
        stockBalance: [],
        workcards:    [],
        invoices:     [],
        bilSel:       [],
        bilRates:     {},
        auth: {
          ...state.auth,
          token: null,
          isAuthenticated: false,
          email: '',
        }
      };

    case 'SET_LOADING':
      return { ...state, loading: { ...state.loading, [action.key]: action.val } };
    case 'SET_ERROR':
      return { ...state, errors: { ...state.errors, [action.key]: action.msg } };
    case 'CLEAR_ERROR':
      return { ...state, errors: { ...state.errors, [action.key]: null } };

    case 'SET_CLIENTS':       return { ...state, clients:      action.data };
    case 'ADD_CLIENT':        return { ...state, clients:      [...state.clients, action.data] };
    case 'UPDATE_CLIENT':     return { ...state, clients:      state.clients.map(x => x.id === action.data.id ? action.data : x) };

    case 'SET_INWARD':        return { ...state, inward:       action.data };
    case 'UPDATE_INWARD_LOCAL': return { ...state, inward:       state.inward.map(x => x.id === action.data.id ? { ...x, ...action.data } : x) };

    case 'SET_LEDGER':        return { ...state, ledger:       action.data };
    case 'SET_STOCK_BALANCE': return { ...state, stockBalance: action.data };

    case 'SET_WORKCARDS':     return { ...state, workcards:    action.data };

    case 'SET_INVOICES':      return { ...state, invoices:     action.data };

    // Billing UI
    case 'TOGGLE_BIL_SEL': {
      const { id } = action.payload;
      const sel = state.bilSel.includes(id)
        ? state.bilSel.filter(x => x !== id)
        : [...state.bilSel, id];
      return { ...state, bilSel: sel };
    }
    case 'SET_BIL_RATE':
      return { ...state, bilRates: { ...state.bilRates, [action.payload.id]: action.payload.val } };
    case 'CLEAR_BILLING':
      return { ...state, bilSel: [], bilRates: {} };

    // Toasts
    case 'ADD_TOAST':
      return { ...state, toasts: [...state.toasts, { id: Date.now(), ...action.payload }] };
    case 'REMOVE_TOAST':
      return { ...state, toasts: state.toasts.filter(t => t.id !== action.payload.id) };

    default:
      return state;
  }
}

// ─────────────────────────────────────────
// Normalizers — map raw API shape → internal shape
// ─────────────────────────────────────────

function normalizeInward(d) {
  return {
    id:           d.id,
    client_id:    d.client_id || null,
    supplier:     d.supplier_name,
    supplier_gstin: d.supplier_gstin || '',
    inv_no:       d.invoice_number,
    inv_date:     d.invoice_date,
    recv_at:      d.received_at,
    total_amt:    d.total_amount,
    total_kg:     d.total_qty_kg || 0,
    status:       d.status,          // 'pending' | 'confirmed' | 'rejected'
    raw_image_url: d.raw_image_url,
    confirmed_by: d.confirmed_by,
    confirmed_at: d.confirmed_at,
    // Nested extracted_data — kept as-is for the panel
    extracted: d.extracted_data || {},
    // Convenient sub-objects for the panel UI
    data: {
      supplier: {
        name:    d.extracted_data?.supplier?.name    || d.supplier_name  || '',
        gstin:   d.extracted_data?.supplier?.gstin   || d.supplier_gstin || '',
        phone:   d.extracted_data?.supplier?.phone   || '',
        address: d.extracted_data?.supplier?.address || '',
      },
      buyer: {
        name:    d.extracted_data?.buyer?.name    || '',
        gstin:   d.extracted_data?.buyer?.gstin   || '',
        address: d.extracted_data?.buyer?.address || '',
      },
      // line_items as returned by Gemini — field names vary
      items:   d.extracted_data?.line_items        || [],
      loading: d.extracted_data?.loading_charges   || 0,
      sgst:    d.extracted_data?.sgst              || 0,
      cgst:    d.extracted_data?.cgst              || 0,
    },
    _raw: d,
  };
}

function normalizeWorkcard(w) {
  return {
    id:           w.id,
    client_id:    w.client_id || null,
    no:           w.ticket_number,
    date:         w.ticket_date,
    recv_at:      w.received_at,
    size:         w.board_size,
    gsm:          w.gsm,
    qty:          w.quantity,
    status:       w.status,            // pending|in_progress|completed|rejected|billed
    deducted:     w.stock_deducted,
    raw_image_url: w.raw_image_url,
    job: {
      designs:    w.job_details?.design_numbers || [],
      offset:     w.job_details?.offset         || '',
      ctp:        w.job_details?.ctp            || '',
      serial:     w.job_details?.serial_name    || '',
      colors:     w.job_details?.colors         || 0,
      paper_type: w.job_details?.paper_type     || '',
      job_type:   w.job_details?.job_type       || '',
      no_stock_tracking: w.job_details?.no_stock_tracking || false,
    },
    _raw: w,
  };
}

function normalizeInvoice(inv) {
  return {
    id:          inv.id,
    client_id:   inv.client_id,
    inv_no:      inv.invoice_number,
    inv_date:    inv.created_at?.split('T')[0],
    wc_ids:      inv.work_card_ids   || [],
    rate:        inv.rate_per_sheet  || 0,
    subtotal:    inv.subtotal        || 0,
    cgst:        inv.cgst            || 0,
    sgst:        inv.sgst            || 0,
    total:       inv.total_amount ? inv.total_amount : ((inv.subtotal || 0) + (inv.cgst || 0) + (inv.sgst || 0)),
    amount_paid: inv.amount_paid     || 0,
    balance_due: inv.balance_due     ?? (inv.total_amount ? inv.total_amount : ((inv.subtotal || 0) + (inv.cgst || 0) + (inv.sgst || 0))),
    status:      inv.status,           // 'sent' | 'partially_paid' | 'paid'
    pdf_url:     inv.pdf_url,
    paid_at:     inv.paid_at,
    _raw:        inv,
  };
}

// ─────────────────────────────────────────
// Error Message Sanitizer
// ─────────────────────────────────────────
function sanitizeErrorMessage(msg) {
  // Map raw backend messages to user-friendly text
  const mappings = [
    // Stock / Inward
    [/Client with GSTIN .+ not found\. Please create the client first via POST \/api\/clients.*/i,
      'Client not found. Please register the client first, then confirm the entry again.'],
    [/No buyer GSTIN found in extracted data.*override endpoint/i,
      'No buyer GSTIN found in the invoice. Please upload a clearer photo or enter the client details manually.'],
    [/gstin is required to create a client.*/i,
      'GSTIN is required to register a new client.'],
    [/Ledger entries already exist.*duplicate confirm prevented/i,
      'This entry has already been confirmed. Duplicate confirmation prevented.'],
    // Work cards
    [/skip_stock_check is only allowed for ad-hoc clients/i,
      'Stock check bypass is only allowed for walk-in (ad-hoc) clients.'],
    [/quantity_sheets must be > 0/i,
      'Quantity must be greater than 0.'],
    [/Work card must have a linked client before approval/i,
      'Please link a client to this work ticket before approving.'],
    [/Stock already deducted for this work card/i,
      'Stock has already been deducted for this ticket.'],
    [/Can only link client to a pending work card/i,
      'Client can only be linked to a pending work ticket.'],
    [/Cannot delete work card.*referenced by an invoice/i,
      'Cannot delete this ticket — it is linked to an invoice.'],
    [/Missing required fields: (.+)/i,
      (_, fields) => `Missing required fields: ${fields}`],
    // Invoices
    [/Only 'sent' or 'partially_paid' invoices can be marked as paid/i,
      'This invoice cannot be marked as paid (it may already be paid or in an invalid state).'],
    [/Invoice is already fully paid/i,
      'This invoice is already fully paid.'],
    [/amount must be > 0/i,
      'Payment amount must be greater than 0.'],
    [/rate_per_sheet must be > 0/i,
      'Rate per sheet must be greater than 0.'],
    [/At least one line item required/i,
      'At least one work ticket must be selected for billing.'],
    [/Work card .+ is not completed/i,
      'One or more work tickets are not yet completed.'],
    [/Work card .+ does not belong to this client/i,
      'One or more work tickets do not belong to this client.'],
    [/Payment exceeds balance due/i,
      'Payment amount exceeds the remaining balance due.'],
    // Clients
    [/Cannot delete client.*has existing (.+)\. Remove those first/i,
      (_, records) => `Cannot delete client — has existing ${records}. Please remove them first.`],
    // Generic
    [/Entry is not in pending status/i,
      'This entry is no longer in pending status.'],
    [/not found/i,
      (match) => match], // Keep "not found" messages as-is (already readable)
  ];

  for (const [pattern, replacement] of mappings) {
    if (pattern.test(msg)) {
      return typeof replacement === 'function' ? msg.replace(pattern, replacement) : replacement;
    }
  }
  // Strip any "via POST/PATCH/DELETE /api/..." references as a catch-all
  return msg.replace(/\s*via\s+(POST|PATCH|DELETE|GET|PUT)\s+\/api\/\S+/gi, '').trim();
}

// ─────────────────────────────────────────
// Provider
// ─────────────────────────────────────────
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // ── Toast ──
  const toast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    dispatch({ type: 'ADD_TOAST', payload: { id, msg, type } });
    setTimeout(() => dispatch({ type: 'REMOVE_TOAST', payload: { id } }), 4500);
  }, []);

  // ── Client lookup helper ──
  const cl = useCallback(
    (id) => state.clients.find(c => c.id === id),
    [state.clients]
  );

  // ── Session Expired/Auth Error Handler ──
  const handleError = useCallback((e, key, label) => {
    const raw = e.message || '';
    const isAuthError = raw.includes('401') ||
                        raw.toLowerCase().includes('not authenticated') ||
                        raw.toLowerCase().includes('invalid token') ||
                        raw.toLowerCase().includes('token expired');

    if (isAuthError) {
      dispatch({ type: 'LOGOUT' });
      toast('Session expired. Please log in again.', 'error');
    } else {
      // Sanitize raw backend messages to user-friendly text
      const friendlyMsg = sanitizeErrorMessage(raw);
      dispatch({ type: 'SET_ERROR', key, msg: friendlyMsg });
      toast(`${label}: ${friendlyMsg}`, 'error');
    }
  }, [toast]);

  // ── Auth Actions ──
  const doLogin = useCallback((payload) => {
    dispatch({ type: 'LOGIN_SUCCESS', payload });
    toast('Logged in successfully', 'success');
  }, [toast]);

  const doLogout = useCallback(() => {
    supabase.auth.signOut();
    dispatch({ type: 'LOGOUT' });
    toast('Logged out successfully', 'success');
  }, [toast]);

  // ─────────────────────────────────────────
  // FETCH functions
  // ─────────────────────────────────────────

  const fetchClients = useCallback(async (params = {}) => {
    dispatch({ type: 'SET_LOADING', key: 'clients', val: true });
    try {
      const data = await api.getClients({ include_adhoc: true, ...params });
      dispatch({ type: 'SET_CLIENTS', data });
      dispatch({ type: 'CLEAR_ERROR', key: 'clients' });
    } catch (e) {
      handleError(e, 'clients', 'Clients');
    } finally {
      dispatch({ type: 'SET_LOADING', key: 'clients', val: false });
    }
  }, [handleError]);

  const fetchInward = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', key: 'inward', val: true });
    try {
      const raw = await api.getInwardStock();
      dispatch({ type: 'SET_INWARD', data: raw.map(normalizeInward) });
      dispatch({ type: 'CLEAR_ERROR', key: 'inward' });
    } catch (e) {
      handleError(e, 'inward', 'Inward Stock');
    } finally {
      dispatch({ type: 'SET_LOADING', key: 'inward', val: false });
    }
  }, [handleError]);

  const fetchLedger = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', key: 'ledger', val: true });
    try {
      // 1. Fetch stock balance across all materials
      const rawBalance = await api.getStockBalanceAll();

      // 2. Fetch clients first if not loaded, to get client IDs for ledger
      let clientsList = state.clients;
      if (!clientsList || clientsList.length === 0) {
        clientsList = await api.getClients({ include_adhoc: true });
        dispatch({ type: 'SET_CLIENTS', data: clientsList });
      }

      // 3. Fetch ledger entries for each client in parallel
      const ledgerResults = await Promise.all(
        clientsList.map(async (c) => {
          try {
            return await api.getLedger(c.id);
          } catch (e) {
            console.error(`Failed to fetch ledger for client ${c.name}:`, e);
            return [];
          }
        })
      );

      // 4. Flatten and normalize movements
      const rawLedger = ledgerResults.flat();
      const ledger = rawLedger.map(r => ({
        id:          r.id,
        material:    r.material,
        size:        r.board_size,
        gsm:         r.gsm,
        qty:         r.quantity_sheets || 0,
        type:        r.transaction_type,   // 'inward' | 'consumed'
        at:          r.movement_at,
        date:        r.transaction_date || r.movement_at?.split('T')[0],
        inward_id:   r.reference_type === 'inward_stock' ? r.reference_id : null,
        workcard_id: r.reference_type === 'work_card' ? r.reference_id : null,
        client_id:   r.client_id,
        _raw:        r,
      }));

      dispatch({ type: 'SET_LEDGER',        data: ledger });
      dispatch({ type: 'SET_STOCK_BALANCE', data: rawBalance });
      dispatch({ type: 'CLEAR_ERROR', key: 'ledger' });
    } catch (e) {
      handleError(e, 'ledger', 'Ledger');
    } finally {
      dispatch({ type: 'SET_LOADING', key: 'ledger', val: false });
    }
  }, [state.clients, handleError]);

  const fetchWorkcards = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', key: 'workcards', val: true });
    try {
      const raw = await api.getWorkcards();
      dispatch({ type: 'SET_WORKCARDS', data: raw.map(normalizeWorkcard) });
      dispatch({ type: 'CLEAR_ERROR', key: 'workcards' });
    } catch (e) {
      handleError(e, 'workcards', 'Work Cards');
    } finally {
      dispatch({ type: 'SET_LOADING', key: 'workcards', val: false });
    }
  }, [handleError]);

  const fetchInvoices = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', key: 'invoices', val: true });
    try {
      const raw = await api.getInvoices();
      dispatch({ type: 'SET_INVOICES', data: raw.map(normalizeInvoice) });
      dispatch({ type: 'CLEAR_ERROR', key: 'invoices' });
    } catch (e) {
      handleError(e, 'invoices', 'Invoices');
    } finally {
      dispatch({ type: 'SET_LOADING', key: 'invoices', val: false });
    }
  }, [handleError]);

  // ─────────────────────────────────────────
  // ACTION helpers
  // ─────────────────────────────────────────

  /** Confirm inward stock → POST /api/stock/inward/{id}/confirm */
  const doConfirmInward = useCallback(async (id, confirmedBy = 'Admin', notes = '') => {
    try {
      const res = await api.confirmInward(id, { confirmed_by: confirmedBy, notes });
      await Promise.all([fetchInward(), fetchLedger()]);
      const entries = res?.ledger_entries?.length || 0;
      toast(`Invoice confirmed — ${entries} ledger entr${entries === 1 ? 'y' : 'ies'} added`, 'success');
      return res;
    } catch (e) {
      const msg = e.message || '';
      if (msg.includes('not found') && msg.includes('GSTIN')) {
        toast('Client not found. Please register the client first, then confirm the entry again.', 'error');
      } else {
        handleError(e, 'inward', 'Confirm failed');
      }
      throw e;
    }
  }, [fetchInward, fetchLedger, toast, handleError]);

  /** Reject inward stock → PATCH /api/stock/inward/{id}/reject */
  const doRejectInward = useCallback(async (id, reason = '') => {
    try {
      await api.rejectInward(id, reason);
      await fetchInward();
      toast('Invoice rejected', 'error');
    } catch (e) {
      handleError(e, 'inward', 'Reject failed');
      throw e;
    }
  }, [fetchInward, toast, handleError]);

  /** Update inward stock → PATCH /api/stock/inward/{id} */
  const doUpdateInward = useCallback(async (id, updates) => {
    // 1. Map fields back to normalized shape for dynamic local state reload
    const localItem = {
      id,
      supplier: updates.supplier_name,
      inv_no: updates.invoice_number,
      inv_date: updates.invoice_date,
      total_amt: updates.total_amount,
      data: updates.extracted_data,
      client_id: updates.client_id || null,
    };
    dispatch({ type: 'UPDATE_INWARD_LOCAL', data: localItem });

    try {
      const res = await api.updateInward(id, updates);
      await fetchInward();
      toast('Inward stock details updated successfully', 'success');
      return res;
    } catch (e) {
      console.warn('Backend update failed, fallback to local state:', e);
      toast('Inward stock updated locally (offline mode)', 'warning');
      return localItem;
    }
  }, [fetchInward, toast]);

  /** Delete inward stock → DELETE /api/stock/inward/{id} */
  const doDeleteInward = useCallback(async (id) => {
    try {
      await api.deleteInward(id);
      await fetchInward();
      toast('Inward stock record deleted', 'success');
    } catch (e) {
      handleError(e, 'inward', 'Delete inward failed');
      throw e;
    }
  }, [fetchInward, toast, handleError]);

  /** Approve work card → PATCH /api/workcards/{id}/approve */
  const doApproveWorkcard = useCallback(async (id) => {
    try {
      const card = state.workcards.find(w => w.id === id);
      await api.approveWorkcard(id);
      await Promise.all([fetchWorkcards(), fetchLedger()]);
      
      if (card?.job?.no_stock_tracking) {
        toast('Walk-in work card approved — no stock deducted', 'success');
      } else {
        toast('Work card approved — stock deducted from ledger', 'success');
      }
    } catch (e) {
      handleError(e, 'workcards', 'Approve failed');
      throw e;
    }
  }, [fetchWorkcards, fetchLedger, toast, handleError]);

  /** Reject work card → PATCH /api/workcards/{id}/reject */
  const doRejectWorkcard = useCallback(async (id) => {
    try {
      await api.rejectWorkcard(id);
      await fetchWorkcards();
      toast('Work card rejected', 'error');
    } catch (e) {
      handleError(e, 'workcards', 'Reject failed');
      throw e;
    }
  }, [fetchWorkcards, toast, handleError]);

  /** Mark in-progress work card as complete → PATCH /api/workcards/{id}/complete */
  const doCompleteWorkcard = useCallback(async (id) => {
    try {
      await api.completeWorkcard(id);
      await fetchWorkcards();
      toast('Work card marked Completed — ready to bill', 'success');
    } catch (e) {
      handleError(e, 'workcards', 'Complete failed');
      throw e;
    }
  }, [fetchWorkcards, toast, handleError]);

  /** Link a client to a work card → PATCH /api/workcards/{id}/link-client */
  const doLinkClient = useCallback(async (workcard_id, client_id) => {
    try {
      await api.linkClientToWorkcard(workcard_id, client_id);
      await fetchWorkcards();
      toast('Client linked successfully', 'success');
    } catch (e) {
      handleError(e, 'workcards', 'Link failed');
      throw e;
    }
  }, [fetchWorkcards, toast, handleError]);

  /** Create a client → POST /api/clients */
  const doCreateClient = useCallback(async (data) => {
    try {
      const { email, ...cleanData } = data;
      const created = await api.createClient(cleanData);
      dispatch({ type: 'ADD_CLIENT', data: created });
      toast(`Client "${created.name}" created`, 'success');
      return created;
    } catch (e) {
      handleError(e, 'clients', 'Create client failed');
      throw e;
    }
  }, [toast, handleError]);

  /** Create an ad-hoc client → POST /api/clients/adhoc */
  const doCreateAdhocClient = useCallback(async (data) => {
    try {
      const { email, ...cleanData } = data;
      for (const key in cleanData) {
        if (cleanData[key] === '') {
          cleanData[key] = null;
        }
      }
      const created = await api.createAdhocClient(cleanData);
      dispatch({ type: 'ADD_CLIENT', data: created });
      toast(`Walk-in client "${created.name}" created`, 'success');
      return created;
    } catch (e) {
      handleError(e, 'clients', 'Create ad-hoc client failed');
      throw e;
    }
  }, [toast, handleError]);

  /** Create/reuse client from inward buyer data → POST /api/stock/inward/{id}/create-client */
  const doCreateClientFromInward = useCallback(async (inwardId, data) => {
    try {
      const res = await api.createClientFromInward(inwardId, data);
      if (!res.already_existed) {
        dispatch({ type: 'ADD_CLIENT', data: res });
        toast(`Client "${res.name}" auto-created`, 'success');
      } else {
        toast(`Linked to existing client "${res.name}"`, 'success');
      }
      return res;
    } catch (e) {
      handleError(e, 'clients', 'Create client from inward failed');
      throw e;
    }
  }, [toast, handleError]);

  /** Delete a client → DELETE /api/clients/{id} */
  const doDeleteClient = useCallback(async (id) => {
    try {
      await api.deleteClient(id);
      await fetchClients();
      toast('Client deleted', 'success');
    } catch (e) {
      handleError(e, 'clients', 'Delete client failed');
      throw e;
    }
  }, [fetchClients, toast, handleError]);

  /** Update a client → PATCH /api/clients/{id} */
  const doUpdateClient = useCallback(async (id, data) => {
    try {
      const { email, ...cleanData } = data;
      const updated = await api.updateClient(id, cleanData);
      dispatch({ type: 'UPDATE_CLIENT', data: updated });
      toast('Client updated', 'success');
      return updated;
    } catch (e) {
      handleError(e, 'clients', 'Update failed');
      throw e;
    }
  }, [toast, handleError]);

  /** Deactivate a client → PATCH /api/clients/{id}/deactivate */
  const doDeactivateClient = useCallback(async (id) => {
    try {
      await api.deactivateClient(id);
      toast('Client deactivated', 'success');
      return true;
    } catch (e) {
      handleError(e, 'clients', 'Deactivation failed');
      throw e;
    }
  }, [toast, handleError]);

  /** Reactivate a client → PATCH /api/clients/{id}/reactivate */
  const doReactivateClient = useCallback(async (id) => {
    try {
      await api.reactivateClient(id);
      toast('Client reactivated', 'success');
      return true;
    } catch (e) {
      handleError(e, 'clients', 'Reactivation failed');
      throw e;
    }
  }, [toast, handleError]);

  /**
   * Generate invoice → POST /api/invoices
   * API expects: { client_id, line_items: [ { work_card_id, rate_per_sheet } ], created_by: 'admin' }
   */
  const doGenerateInvoice = useCallback(async ({ client_id, line_items }) => {
    try {
      const inv = await api.createInvoice({
        client_id,
        line_items: line_items.map(item => ({
          work_card_id: item.work_card_id,
          rate_per_sheet: Number(item.rate_per_sheet),
        })),
        created_by: 'admin',
      });
      await Promise.all([fetchInvoices(), fetchWorkcards()]);
      dispatch({ type: 'CLEAR_BILLING' });
      toast(`Invoice ${inv.invoice_number} generated — ₹${Number(inv.total || inv.total_amount || 0).toLocaleString('en-IN')}`, 'success');
      return inv;
    } catch (e) {
      handleError(e, 'invoices', 'Invoice failed');
      throw e;
    }
  }, [fetchInvoices, fetchWorkcards, toast, handleError]);

  /** Mark invoice paid → PATCH /api/invoices/{id}/paid */
  const doMarkPaid = useCallback(async (id) => {
    try {
      const res = await api.markInvoicePaid(id);
      await fetchInvoices();
      toast('Invoice marked as Paid', 'success');
      return res;
    } catch (e) {
      handleError(e, 'invoices', 'Mark paid failed');
      throw e;
    }
  }, [fetchInvoices, toast, handleError]);

  /** Record a payment (partial or full) → POST /api/invoices/{id}/payments */
  const doRecordPayment = useCallback(async (invoiceId, { amount, method, note }) => {
    try {
      const res = await api.recordInvoicePayment(invoiceId, { amount: Number(amount), method, note });
      await fetchInvoices();
      toast(`Payment of ₹${Number(amount).toLocaleString('en-IN')} recorded`, 'success');
      return res;
    } catch (e) {
      handleError(e, 'invoices', 'Payment failed');
      throw e;
    }
  }, [fetchInvoices, toast, handleError]);

  /** Delete invoice → DELETE /api/invoices/{id} */
  const doDeleteInvoice = useCallback(async (id) => {
    try {
      await api.deleteInvoice(id);
      await Promise.all([fetchInvoices(), fetchWorkcards()]);
      toast('Invoice deleted — work cards reverted to completed', 'success');
    } catch (e) {
      handleError(e, 'invoices', 'Delete invoice failed');
      throw e;
    }
  }, [fetchInvoices, fetchWorkcards, toast, handleError]);

  /** Create manual work card → POST /api/workcards */
  const doCreateWorkcard = useCallback(async (data) => {
    try {
      const res = await api.createWorkcard(data);
      await Promise.all([fetchWorkcards(), fetchLedger()]);
      toast(`Manual work card #${res.ticket_number || 'new'} created`, 'success');
      return res;
    } catch (e) {
      handleError(e, 'workcards', 'Create work card failed');
      throw e;
    }
  }, [fetchWorkcards, fetchLedger, toast, handleError]);

  /** Delete work card → DELETE /api/workcards/{id} */
  const doDeleteWorkcard = useCallback(async (id) => {
    try {
      await api.deleteWorkcard(id);
      await fetchWorkcards();
      toast('Work card deleted', 'success');
    } catch (e) {
      handleError(e, 'workcards', 'Delete work card failed');
      throw e;
    }
  }, [fetchWorkcards, toast, handleError]);

  /** Adjust stock manually → PATCH /api/stock/adjust */
  const doAdjustStock = useCallback(async (data) => {
    try {
      const res = await api.adjustStock(data);
      await fetchLedger();
      toast(`Stock adjusted successfully`, 'success');
      return res;
    } catch (e) {
      handleError(e, 'ledger', 'Stock adjustment failed');
      throw e;
    }
  }, [fetchLedger, toast, handleError]);

  // ─────────────────────────────────────────
  // Keep the local session in sync with Supabase's own token
  // refresh/rotation, so the JWT held in localStorage never goes stale
  // while the tab is open.
  // ─────────────────────────────────────────
  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        dispatch({ type: 'TOKEN_REFRESHED', payload: { token: session.access_token } });
      } else if (event === 'SIGNED_OUT') {
        dispatch({ type: 'LOGOUT' });
      }
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  // ─────────────────────────────────────────
  // Bootstrap on mount
  // ─────────────────────────────────────────
  useEffect(() => {
    if (state.auth.isAuthenticated) {
      fetchClients();
      fetchInward();
      fetchLedger();
      fetchWorkcards();
      fetchInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.auth.isAuthenticated]);

  const value = {
    state,
    dispatch,
    toast,
    cl,
    // Fetchers (for manual refresh)
    fetchClients,
    fetchInward,
    fetchLedger,
    fetchWorkcards,
    fetchInvoices,
    // Actions
    doLogin,
    doLogout,
    doConfirmInward,
    doRejectInward,
    doUpdateInward,
    doDeleteInward,
    doApproveWorkcard,
    doRejectWorkcard,
    doCompleteWorkcard,
    doLinkClient,
    doCreateClient,
    doCreateAdhocClient,
    doCreateClientFromInward,
    doDeleteClient,
    doUpdateClient,
    doDeactivateClient,
    doReactivateClient,
    doGenerateInvoice,
    doMarkPaid,
    doRecordPayment,
    doDeleteInvoice,
    doCreateWorkcard,
    doDeleteWorkcard,
    doAdjustStock,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
